import { useEffect, useRef, type ReactElement, type ReactNode } from "react";
import { UiButton, UiStatus } from "@kuroo/ui";
import { providerPhotoUrl } from "./provider-photo";

export function Loading({ label = "Cargando…" }: { label?: string }): ReactElement {
  return <div className="state state-loading" role="status"><span className="spinner" aria-hidden="true" />{label}</div>;
}

export function ErrorState({ message, retry }: { message: string; retry?: () => void }): ReactElement {
  return <UiStatus tone="error" className="state state-error"><strong>No se pudo cargar esta sección</strong><span>{message}</span>{retry && <UiButton tone="secondary" size="sm" className="button secondary" onClick={retry}>Reintentar</UiButton>}</UiStatus>;
}

export function Empty({ children }: { children: ReactNode }): ReactElement { return <div className="state state-empty"><strong>Sin datos todavía</strong><span>{children}</span></div>; }

export function useDialogFocusTrap(dialogRef: { current: HTMLElement | null }, onClose: () => void, disabled = false): void {
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);
  useEffect(() => {
    if (disabled) return;
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const focusable = () => Array.from(dialogRef.current?.querySelectorAll<HTMLElement>("button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href]") ?? []);
    focusable()[0]?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") { event.preventDefault(); onCloseRef.current(); return; }
      if (event.key !== "Tab" || !dialogRef.current) return;
      const elements = focusable();
      if (!elements.length) return;
      const first = elements[0]; const last = elements[elements.length - 1];
      if (!dialogRef.current.contains(document.activeElement)) { event.preventDefault(); first.focus(); }
      else if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => { document.removeEventListener("keydown", onKeyDown); previous?.focus(); };
  }, [dialogRef, disabled]);
}

export function PageHeader({ eyebrow, title, description, action }: { eyebrow: string; title: string; description?: string; action?: ReactNode }): ReactElement { return <header className="page-header"><div><p className="eyebrow">{eyebrow}</p><h2>{title}</h2>{description && <p className="lede">{description}</p>}</div>{action}</header>; }

export function ProviderAvatar({ firstName, photoUrl, color, large = false }: { firstName?: string; photoUrl?: string | null; color?: string | null; large?: boolean }): ReactElement {
  const source = providerPhotoUrl(photoUrl);
  return <span className={`avatar${large ? " large" : ""}`} style={{ background: color ?? undefined }}>{source ? <img src={source} alt="" /> : (firstName?.[0] ?? "P")}</span>;
}
